import type React from 'react';
import { useEffect, useState } from 'react';
import {
  AsyncKovResourcePicker,
  KovResourcePicker,
  Modal,
  useLoadSriOptions,
} from '@kathondvla/react-shared-components/src/components';
import type { ContentHref } from '@generalTypes/apiTypes';
import { contentApi } from '@store/api/apiConfig';
import { formatDate, stripHtml } from '@newStore/genericHelpers';
import { fetchAllCmd } from '@store/commands/documentCommands';
import { getGoalIdentifier, getResourceKey, sortByReadorder } from '@store/helpers/documentHelpers';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { selectRootHref } from '@newStore/documentUI/documentUISelectors';
import { loadPreviousVersion } from '@newStore/llinkid/llinkidActions';
import { selectPreviousVersionContent } from '@newStore/llinkid/llinkidSelectors';
import { useDispatch } from 'react-redux';

const realisationTypes = [
  {
    value: 'PARTIALLY_IMPLEMENTS',
    title: 'Gedeeltelijk',
  },
  {
    value: 'IMPLEMENTS',
    title: 'Integraal',
  },
];

/**
 * Ideally we want to put the whole document into the state. 
 * It is like the ODET reference frame, but for the Llinkid goals.
 * ODET is also not a real reference frame.
 * the problem is that the aside is getting its data from the externalData state. 
 * this means the aside, right now, will refetch the same data again.
 * i think in the final solution, the reference frame state should merge into the externalData state.
 */
const getGoals = async (rootHref) => {
  const goals = await fetchAllCmd(
    {
      root: rootHref,
      type: 'LLINKID_GOAL',
      limit: 500,
    },
    { fetchTreeAsLeaf: true },
  );
  return sortByReadorder(goals).map((goal) => ({
    display: `${getGoalIdentifier({
      goal,
      relations: goal.$$treeAsLeaf,
      isOdet: false,
    })} - ${stripHtml(goal.description)}`,
    href: goal.$$meta.permalink,
  }));
};

export interface LlinkidGoalRelationsModalProps {
  selectedValues?: string[];
  multipleRelationTypes?: boolean;
  defaultCurriculumHref?: ContentHref | null;
  onClose?: () => void;
  onSubmit?: (response: { goalHref: ContentHref; realisation: string }) => void;
}


const LlinkidGoalRelationsModal: React.FC<LlinkidGoalRelationsModalProps> = ({
  selectedValues = [],
  multipleRelationTypes = false,
  defaultCurriculumHref = null,
  onClose = () => {},
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const [selectedDocument, setSelectedDocument] = useState<ContentHref | null>(defaultCurriculumHref);
  const [selectedGoal, setSelectedGoal] = useState<ContentHref | undefined>();
  const [selectedRealisation, setSelectedRealisation] = useState<string>('IMPLEMENTS');

  const [goals, setGoals] = useState<
    | {
        display: string;
        href: ContentHref;
      }[]
    | null
  >([]);

  const selectionOk = selectedGoal && selectedDocument && selectedRealisation;

  useEffect(() => {
    if(selectedDocument) {
      setGoals(null);
      getGoals(selectedDocument).then((goals) => {
        setGoals(goals.filter((g) => !selectedValues.includes(getResourceKey(g.href))));
      });
    }
  }, [selectedDocument, selectedValues]);

  const onChangeDocument = (o) => {
    setSelectedDocument(o.href);    
  };

  const onChangeGoal = (o) => {
    setSelectedGoal(o.href);
  };

  const onChangeRealisation = (o) => {
    setSelectedRealisation(o.value);
  };

  const handleSubmit = () => {
    if (onSubmit && selectedGoal) {
      onSubmit({
        goalHref: selectedGoal,
        realisation: selectedRealisation,
      });
    }
    onClose();
  };

  const rootHref = useTypedSelector((state) => selectRootHref(state));
  useEffect(() => {
    if (rootHref) {
      dispatch(loadPreviousVersion({ href: rootHref }));
    }
  }, [dispatch, rootHref]);
  const documents = useTypedSelector(
    (state) => rootHref && selectPreviousVersionContent(state, rootHref),
  );


  const sriLoadOptionsDocument = useLoadSriOptions({
    sriClient: contentApi,
    href: '/content',
    parameters: {
      type: 'LLINKID_CURRICULUM',
      orderBy: 'title',
      limit: 6000,
    },
  });

  const formatDocument = (o) => {
    const option = o.$$expanded || o;
    let title = option.title;
    if (option.identifiers) {
      title += ` ${option.identifiers[0]}  `;
    }
    if (option.version) {
      title += ` (v${option.version.major})`;
    }
    if (option.issued) {
      title += ` (${formatDate(option.issued, false)})`;
    }
    return <div className="picker-result">{title}</div>;
  };

  const formatGoal = (o) => {
    const option = o.$$expanded || o;
    return option.display;
  };

  return (
    <div className="overflown-modal">
      <Modal.Header title={'Gerelateerde doelen ander leerplan'} onClose={onClose} />
      <Modal.Body>
        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <label className="control-label">Document:</label>
              {defaultCurriculumHref ? (
                <KovResourcePicker
                  clearable={false}
                  multiple={false}
                  disabled={true}
                  placeholder={documents?.[0] ? formatDocument(documents[0]) : 'Laden...'}
                />
              ) : (
              <AsyncKovResourcePicker
                defaultValue={selectedDocument}
                onChange={onChangeDocument}
                loadOptions={sriLoadOptionsDocument}
                localData={true}
                optionValue={(option) => option.href}
                placeholder={'Kies een leerplan secundair'}
                clearable={false}
                multiple={false}
                optionTemplate={formatDocument}
              />
              )}        
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <label className="control-label">Gerelateerd doel:</label>
              {goals ? (
                <KovResourcePicker
                  defaultValue={selectedGoal}
                  onChange={onChangeGoal}
                  localData={goals}
                  optionValue={(option) => option.href}
                  placeholder={'Kies een gerelateerd doel'}
                  clearable={false}
                  multiple={false}
                  optionTemplate={formatGoal}
                  disabled={!selectedDocument || goals.length === 0}
                />
              ) : (
                <div className="shimmer narrowLine" />
              )}
            </div>
          </div>
        </div>
        {multipleRelationTypes && (
          <div className="form-group">
            <div className="row">
              <div className="col-md-12">
                <label className="control-label">Realisatie: </label>
                <KovResourcePicker
                  defaultValue={realisationTypes.find((t) => t.value === selectedRealisation)}
                  onChange={onChangeRealisation}
                  localData={realisationTypes}
                  optionValue={(option) => option.value}
                  placeholder={'Kies een realisatie'}
                  clearable={false}
                  multiple={false}
                  optionTemplate={(o) => o.title}
                />
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link pull-left" onClick={onClose}>
          Annuleren
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={!selectionOk}>
          Ok
        </button>
      </Modal.Footer>
    </div>
  );
};

LlinkidGoalRelationsModal.displayName = 'LlinkidGoalRelationsModal';

export default LlinkidGoalRelationsModal;
